import { countryName } from './countries.js';

/**
 * 366 (#1223): typing help for the staff Change dialog — the same refusal sentences the platform sends, so staff see
 * them before saving. The backend (`users/identityNumber.ts`) is the authority.
 */

export type IdentityKind = 'mykad' | 'passport';

export interface IdentityNumberValue {
  kind: IdentityKind;
  country?: string;
  number: string;
}

export interface IdentityNumberSummary {
  kind: IdentityKind;
  country: string;
  masked: string;
}

export const INVALID_MYKAD = "This isn't a valid MyKad number.";
export const INVALID_PASSPORT =
  "Enter the passport's country and number (5 to 20 letters or digits).";

export function formatMykad(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 12);
  return [d.slice(0, 6), d.slice(6, 8), d.slice(8, 12)].filter(Boolean).join('-');
}

function realDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const t = new Date(Date.UTC(year, month - 1, day));
  return t.getUTCFullYear() === year && t.getUTCMonth() === month - 1 && t.getUTCDate() === day;
}

export function identityNumberError(value: IdentityNumberValue): string | null {
  if (value.kind === 'mykad') {
    const d = value.number.replace(/[\s-]/g, '');
    if (!/^\d{12}$/.test(d)) return INVALID_MYKAD;
    const yy = Number(d.slice(0, 2));
    const month = Number(d.slice(2, 4));
    const day = Number(d.slice(4, 6));
    return realDate(1900 + yy, month, day) || realDate(2000 + yy, month, day)
      ? null
      : INVALID_MYKAD;
  }
  const number = value.number.replace(/[\s-]/g, '').toUpperCase();
  const country = (value.country ?? '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(country) && /^[A-Z0-9]{5,20}$/.test(number) ? null : INVALID_PASSPORT;
}

export function identityNumberBody(value: IdentityNumberValue) {
  return value.kind === 'mykad'
    ? { kind: 'mykad' as const, number: value.number }
    : { kind: 'passport' as const, country: value.country ?? '', number: value.number };
}

/** The tag beside the number: "MyKad", or "Passport · Indonesia". */
export function identityKindLabel(value: { kind: IdentityKind; country: string }): string {
  return value.kind === 'mykad' ? 'MyKad' : `Passport · ${countryName(value.country)}`;
}

/** A revealed number shown like the card: a MyKad with its dashes, a passport as stored. */
export function displayNumber(value: { kind: IdentityKind; number: string }): string {
  return value.kind === 'mykad' ? formatMykad(value.number) : value.number;
}
